import React, { RefObject } from 'react';
import { FormikProps } from 'formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { postTenantsToGroup } from 'Services';
import { RQ_ADD_TENANTS_TO_TENANT_GROUP, RQ_TENANT_GROUPS, RQ_TENANT_GROUP_TENANTS } from 'Utils';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { getTranslationErrorHandling } from '../helper';

type Props = {
    formRef: RefObject<FormikProps<TenantCheckboxTableForm>>;
    tenantGroupId: string;
    tenantGroupName?: string;
};

const useAddTenantsToTenantGroup = ({ formRef, tenantGroupId, tenantGroupName }: Props) => {
    const { t } = useTranslation();
    const tenantGroupEntryKey = getTranslationErrorHandling(t, 'tenantGroup');
    const { addAlert } = useAlertBanner();
    const { addTechnicalErrorModal, clearAll } = useReduxModal();
    const history = useHistory();
    const queryClient = useQueryClient();

    const handleOnSuccess = (variable: { tenantKeys: string[] }) => {
        addAlert({
            id: 'save-tenants-to-tenantGroup-success',
            content: t('postAddTenantsToTenantGroupSuccessMsg', {
                count: variable.tenantKeys.length,
                tenantGroupName
            }),
            status: 'success'
        });
        queryClient.invalidateQueries([RQ_TENANT_GROUP_TENANTS, tenantGroupId]);

        if (formRef.current) {
            formRef.current.setFieldValue('headerCheckboxSelected', false);
        }

        history.push(`/tenants/tenant-group/${tenantGroupId}`);
    };

    const handle409Error = () => {
        addTechnicalErrorModal(
            '',
            tenantGroupEntryKey('addTenantsErrorModalTitle'),
            () => {
                clearAll();
            },
            {
                status: 'error',
                content: (
                    <Trans i18nKey='errorHandling.tenantGroup.tenantsAlreadyAddedMsg' values={{ tenantGroupName }} />
                ),
                cancelText: tenantGroupEntryKey('Reselect tenants')
            }
        );
    };

    const handle404Error = () => {
        addTechnicalErrorModal(
            '',
            t('Tenant Group Unavailable'),
            () => {
                queryClient.invalidateQueries([RQ_TENANT_GROUPS]);
                clearAll();
                history.push('/tenants');
            },
            {
                status: 'error',
                content: t('tenantGroupDeletedMsg', { tenantGroupName }),
                cancelText: t('Return to Manage Tenants')
            }
        );
    };

    // display BE API error messages
    const handle400Error = (err: PostTenantsToGroupAPIError) => {
        addTechnicalErrorModal(
            tenantGroupEntryKey('addTenantsAPIError', { errMsg: err.data?.error?.message ?? err.data?.message }),
            tenantGroupEntryKey('addTenantsErrorModalTitle'),
            () => {
                clearAll();
            }
        );
    };

    return useMutation(
        [RQ_ADD_TENANTS_TO_TENANT_GROUP],
        (payload: PostTenantsToGroupPayload) => postTenantsToGroup(Number(tenantGroupId), payload),
        {
            onSuccess: (_data, variable: { tenantKeys: string[] }) => {
                handleOnSuccess(variable);
            },
            onError: (err: PostTenantsToGroupAPIError) => {
                switch (err.data.statusCode) {
                    case 409:
                        handle409Error();
                        break;
                    case 404:
                        handle404Error();
                        break;
                    case 400:
                        handle400Error(err);
                        break;
                    default:
                        addTechnicalErrorModal();
                        break;
                }
            }
        }
    );
};

export default useAddTenantsToTenantGroup;
